import type { SVGProps } from 'react';

type P = SVGProps<SVGSVGElement> & { size?: number };

const base = (size: number, props: P) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

export const Logo = ({ size = 34 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 34 34" aria-hidden="true">
    <rect width="34" height="34" rx="10" fill="#FF5A4E" />
    <path d="M9 11.5c0-1.4 1.1-2.5 2.5-2.5h11c1.4 0 2.5 1.1 2.5 2.5v8c0 1.4-1.1 2.5-2.5 2.5H15l-4.5 3.5V22h1c-1.4 0-2.5-1.1-2.5-2.5z" fill="#1A0B0A" />
    <path d="M14 14.5v3M17 13v6M20 14.5v3" stroke="#FF5A4E" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export const Globe = ({ size = 18, ...p }: P) => (
  <svg {...base(size, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
  </svg>
);

export const Speaker = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);

export const SpeakerSmall = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
  </svg>
);

export const Chevron = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export const Back = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="m15 6-6 6 6 6" />
  </svg>
);

export const Expand = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

export const Collapse = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
  </svg>
);

export const Sliders = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </svg>
);

export const Bell = ({ size = 22, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
  </svg>
);

export const Play = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 5v14l12-7z" />
  </svg>
);

export const Pause = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

export const SkipNext = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M5 5v14l10-7zM17 5h2.5v14H17z" />
  </svg>
);

export const Redo = ({ size = 20, ...p }: P) => (
  <svg {...base(size, p)}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" />
  </svg>
);

export const Wave = ({ paused = false, big = false }: { paused?: boolean; big?: boolean }) => (
  <span className={`wave${big ? ' wave--big' : ''}${paused ? ' wave--paused' : ''}`} aria-hidden="true">
    <i />
    <i />
    <i />
    <i />
    <i />
    {big && <i />}
    {big && <i />}
  </span>
);
